import { PhoneNumberUtil } from 'google-libphonenumber';
import { CountryISO } from 'ngx-intl-tel-input';

const phoneUtil = PhoneNumberUtil.getInstance();

export interface PhoneImportError {
  row: number;
  field?: string;
  message: string;
}

export function digitsOnly(value: unknown): string {
  return String(value ?? '').replace(/\D/g, '');
}

export function resolvePhone(countryCode: unknown, phone: unknown): {
  ok: boolean;
  hasPhone: boolean;
  countryCode: string | null;
  nationalNumber: string | null;
  error?: string;
} {
  const cc = digitsOnly(countryCode);
  const raw = String(phone ?? '').trim();
  const phoneDigits = digitsOnly(raw);

  if (!phoneDigits) {
    if (raw) {
      return { ok: false, hasPhone: true, countryCode: cc || null, nationalNumber: null, error: 'Phone number is not valid' };
    }
    return { ok: true, hasPhone: false, countryCode: cc || null, nationalNumber: null };
  }

  if (!cc) {
    return { ok: false, hasPhone: true, countryCode: null, nationalNumber: null, error: 'Country code is required' };
  }

  const candidates: string[] = [];
  const push = (value: string) => {
    if (value && !candidates.includes(value)) candidates.push(value);
  };
  if (phoneDigits.startsWith(cc) && phoneDigits.length > cc.length) push(`+${phoneDigits}`);
  push(`+${cc}${phoneDigits}`);
  const withoutTrunk = phoneDigits.replace(/^0+/, '');
  if (withoutTrunk && withoutTrunk !== phoneDigits) push(`+${cc}${withoutTrunk}`);

  for (const candidate of candidates) {
    try {
      const parsed = phoneUtil.parse(candidate);
      if (!phoneUtil.isValidNumber(parsed)) continue;
      if (String(parsed.getCountryCode()) !== cc) continue;
      return {
        ok: true,
        hasPhone: true,
        countryCode: cc,
        nationalNumber: String(parsed.getNationalNumber()),
      };
    } catch {
      continue;
    }
  }

  return {
    ok: false,
    hasPhone: true,
    countryCode: cc,
    nationalNumber: null,
    error: `Phone number is not valid for country code ${cc}`,
  };
}

export function regionForDialCode(countryCode: unknown): CountryISO {
  const cc = Number(digitsOnly(countryCode));
  if (!cc) return '' as CountryISO;
  const region = phoneUtil.getRegionCodeForCountryCode(cc);
  const iso = region && region !== 'ZZ' ? region.toLowerCase() : '';
  return iso as CountryISO;
}

export function phoneControlValue(countryCode: unknown, phone: unknown): string {
  const digits = digitsOnly(phone);
  if (!digits) return '';
  const cc = digitsOnly(countryCode);
  if (cc) {
    const resolved = resolvePhone(cc, phone);
    if (resolved?.ok && resolved.hasPhone && resolved.nationalNumber) {
      return `+${cc}${resolved.nationalNumber}`;
    }
    const national = (digits.startsWith(cc) && digits.length > cc.length ? digits.slice(cc.length) : digits).replace(/^0+/, '');
    return `+${cc}${national}`;
  }
  return digits;
}

function parseInternational(value: string): { countryCode: string; nationalNumber: string } | null {
  const raw = String(value || '').trim();
  const digits = digitsOnly(raw);
  if (!digits) return null;
  try {
    const parsed = phoneUtil.parse(raw.startsWith('+') ? raw : `+${digits}`);
    if (!phoneUtil.isValidNumber(parsed)) return null;
    return {
      countryCode: String(parsed.getCountryCode()),
      nationalNumber: String(parsed.getNationalNumber()),
    };
  } catch {
    return null;
  }
}

export function assignIntlPhone(target: Record<string, any>, phoneKey: string, value: any): void {
  if (value == null || value === '') return;
  if (typeof value === 'string') {
    const parsed = parseInternational(value);
    if (!parsed) return;
    target['country_code'] = parsed.countryCode;
    target[phoneKey] = parsed.nationalNumber;
    return;
  }
  if (!value.e164Number) {
    target[phoneKey] = '';
    target['country_code'] = '';
    return;
  }
  const dial = digitsOnly(value.dialCode);
  const e164 = digitsOnly(value.e164Number);
  let national = dial && e164.startsWith(dial) ? e164.slice(dial.length) : '';
  if (!national) {
    national = digitsOnly(value.nationalNumber);
    if (dial && national.startsWith(dial)) national = national.slice(dial.length);
  }
  target['country_code'] = dial;
  target[phoneKey] = national.replace(/^0+/, '');
}

export const phoneImportToastOptions = {
  timeOut: 12000,
  enableHtml: true,
  closeButton: true,
  toastClass: 'ngx-toastr phone-import-toast',
};

function escapeToastHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatRowSpan(rows: number[]): string {
  const unique = [...new Set(rows)].filter((row) => Number.isFinite(row)).sort((a, b) => a - b);
  if (!unique.length) return '';
  const shown = unique.slice(0, 12);
  const parts: string[] = [];
  let start = shown[0];
  let previous = shown[0];
  const flush = (end: number) => {
    parts.push(start === end ? String(start) : `${start}–${end}`);
  };
  for (let index = 1; index < shown.length; index++) {
    if (shown[index] === previous + 1) {
      previous = shown[index];
      continue;
    }
    flush(previous);
    start = shown[index];
    previous = shown[index];
  }
  flush(previous);
  const extra = unique.length - shown.length;
  const label = unique.length === 1 ? 'Row' : 'Rows';
  const list = parts.join(', ');
  return extra > 0 ? `${label} ${list} and ${extra} more` : `${label} ${list}`;
}

export function formatPhoneImportErrors(errors: PhoneImportError[]): string {
  const groups = new Map<string, number[]>();
  errors.forEach((error) => {
    const message = error.message || 'Invalid phone number';
    const rows = groups.get(message) || [];
    rows.push(error.row);
    groups.set(message, rows);
  });

  const blocks = [...groups.entries()].slice(0, 6).map(([message, rows]) => {
    return `<div class="phone-import-error"><div class="phone-import-error-msg">${escapeToastHtml(message)}</div><div class="phone-import-error-rows">${escapeToastHtml(formatRowSpan(rows))}</div></div>`;
  });
  const hidden = groups.size - blocks.length;
  if (hidden > 0) {
    blocks.push(`<div class="phone-import-error-more">${hidden} more issue${hidden === 1 ? '' : 's'}</div>`);
  }
  return blocks.join('');
}

export function readImportErrors(error: any): PhoneImportError[] {
  const errors = error?.error?.errors;
  if (!Array.isArray(errors)) return [];
  return errors.map((item: any, index: number) => ({
    row: Number(item?.row) || index + 1,
    field: item?.field,
    message: item?.message || 'Invalid phone number',
  }));
}
